// ══════════════════════════════════════════════════════════════
// AUTH MODAL CONTROLLER (GOOGLE 1-CLICK AUTHENTICATION)
// ══════════════════════════════════════════════════════════════

import { store } from '../store.js';
import { showToast, renderIcons } from '../uiUtils.js';
import { getAuthModalTemplate } from './authTemplate.js';

let isDOMInitialized = false;

export function ensureAuthDOM() {
  if (document.getElementById('auth-modal')) return;
  const container = document.createElement('div');
  container.id = 'auth-modal-container';
  container.innerHTML = getAuthModalTemplate();
  document.body.appendChild(container);
  renderIcons();
}

export function initAuthModule() {
  ensureAuthDOM();
  if (isDOMInitialized) return;
  isDOMInitialized = true;

  const authModal = document.getElementById('auth-modal');
  const closeBtn = document.getElementById('close-auth-modal-btn');

  // Close handlers
  closeBtn?.addEventListener('click', closeAuthModal);
  authModal?.addEventListener('click', (e) => {
    if (e.target === authModal) closeAuthModal();
  });

  // Keyboard Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && authModal && !authModal.classList.contains('hidden')) {
      closeAuthModal();
    }
  });

  // Check Redirect Result & Auth State on startup
  import('../firebase.js').then(async ({ getAuthHelpers }) => {
    try {
      const { auth, getRedirectResult, onAuthStateChanged } = await getAuthHelpers();
      if (!auth) return;

      // Handle return from redirect flow
      if (getRedirectResult) {
        const redirectRes = await getRedirectResult();
        if (redirectRes?.user) {
          const user = await store.saveVerifiedUser(redirectRes.user);
          closeAuthModal();
          showToast(`🎉 Xin chào ${user.name}! Đăng nhập thành công.`);
          window.dispatchEvent(new CustomEvent('auth-state-changed', { detail: user }));
        }
      }

      // Realtime listener: auto-dismiss dialog instantly when user state is confirmed
      if (onAuthStateChanged) {
        onAuthStateChanged(async (firebaseUser) => {
          if (firebaseUser) {
            closeAuthModal();
            const user = await store.saveVerifiedUser(firebaseUser);
            window.dispatchEvent(new CustomEvent('auth-state-changed', { detail: user }));
          }
        });
      }
    } catch (err) {
      console.warn('[Auth Initialization/Redirect Check]', err);
    }
  }).catch(() => {});

  // Google 1-Click Sign-in / Sign-up
  const googleBtn = document.getElementById('btn-quick-google');
  const googleBtnText = document.getElementById('btn-quick-google-text');

  googleBtn?.addEventListener('click', async () => {
    try {
      if (googleBtnText) googleBtnText.textContent = 'Đang kết nối Google...';
      googleBtn.classList.add('opacity-70', 'pointer-events-none');
      showToast('Đang mở đăng nhập Google...', 'info');

      const user = await store.loginWithGoogle();
      if (user) {
        closeAuthModal();
        showToast(`🎉 Xin chào ${user.name}! Đăng nhập thành công.`);
        window.dispatchEvent(new CustomEvent('auth-state-changed', { detail: user }));
      }
    } catch (err) {
      if (err.message?.includes('đóng cửa sổ') || err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        showToast('Bạn đã đóng cửa sổ đăng nhập Google.', 'info');
      } else {
        showToast(err.message || 'Đăng nhập Google thất bại!', 'error');
      }
    } finally {
      if (googleBtnText) googleBtnText.textContent = 'Đăng nhập bằng Google';
      googleBtn?.classList.remove('opacity-70', 'pointer-events-none');
    }
  });
}

export function openAuthModal() {
  if (store.getCurrentUser()) {
    showToast('Bạn đang trong trạng thái đăng nhập.', 'info');
    return;
  }

  initAuthModule();
  const modal = document.getElementById('auth-modal');
  if (!modal) return;

  modal.classList.remove('hidden');
  modal.classList.add('flex');
  document.body.classList.add('overflow-hidden');
  document.documentElement.classList.add('overflow-hidden');
}

export function closeAuthModal() {
  const modal = document.getElementById('auth-modal');
  if (!modal) return;

  modal.classList.add('hidden');
  modal.classList.remove('flex');
  document.body.classList.remove('overflow-hidden');
  document.documentElement.classList.remove('overflow-hidden');
}

// Global exposure
if (typeof window !== 'undefined') {
  window.openAuthModal = openAuthModal;
  window.closeAuthModal = closeAuthModal;
}
